import React from 'react';

import { Box, VStack } from '@rbilabs/universal-components';
import { closestTo, format, parse } from 'date-fns';
import { DAYPART_FORMAT, TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import { capitalize } from 'lodash-es';

import Picture from 'components/picture';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { IValidDayPart } from 'state/menu/main-menu/types';

interface IProps {
  dayParts: ReadonlyArray<IValidDayPart>;
  itemDayParts: string[];
  marginAndBorders: boolean;
}

const ContentWrapper = Box.withConfig({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  width: 'full',
  maxWidth: {
    base: '$64',
    md: '$72',
  },
});

const TextWrapper = VStack.withConfig({
  justifyContent: 'center',
});

const ItemUnavailableMessage = ({ dayParts, itemDayParts, marginAndBorders }: IProps) => {
  const now = new Date();
  const itemDayPartObjects = dayParts.filter(part => itemDayParts.includes(part.key));
  const itemDayPartBoundaryDateObjects = itemDayPartObjects.flatMap(part => [
    parse(part.startTime, DAYPART_FORMAT, now),
    parse(part.endTime, DAYPART_FORMAT, now),
  ]);
  const closestDayPartBoundaryDateObj = closestTo(now, itemDayPartBoundaryDateObjects);

  if (!closestDayPartBoundaryDateObj) {
    return null;
  }

  const closestDayPartBoundaryString = format(closestDayPartBoundaryDateObj, DAYPART_FORMAT);
  const closestItemDayPartObject = itemDayPartObjects.find(part =>
    Object.values(part).some(time => time === closestDayPartBoundaryString)
  );

  if (!closestItemDayPartObject) {
    return null;
  }

  const { startTime, endTime, key, displayName, icon } = closestItemDayPartObject;
  const dayPartName = displayName?.locale || key;

  let dayPartMessage = '';
  let timeBoundaryMessage = '';
  const nowInDayPartFormat = format(now, DAYPART_FORMAT);

  if (nowInDayPartFormat < startTime) {
    dayPartMessage = t('daypartBeforeStartTime', { dayPart: dayPartName.toLowerCase() });
    timeBoundaryMessage = t('daypartStartsAt', {
      dayPart: capitalize(dayPartName),
      startTime: format(parse(startTime, DAYPART_FORMAT, now), TWELVE_HOUR_TIME_PARSE_FORMAT),
    });
  } else {
    dayPartMessage = t('daypartAfterEndTime', { dayPart: dayPartName.toLowerCase() });
    timeBoundaryMessage = t('daypartEndsAt', {
      dayPart: capitalize(dayPartName),
      endTime: format(parse(endTime, DAYPART_FORMAT, now), TWELVE_HOUR_TIME_PARSE_FORMAT),
    });
  }

  return (
    <ContentWrapper>
      <Box flex={0.3}>
        <Picture image={icon} alt={dayPartName} width={'40px'} />
      </Box>
      <Box flex={0.7}>
        <TextWrapper>
          {dayPartMessage && (
            <Typography variant="headerThree" m="$0">
              {dayPartMessage}
            </Typography>
          )}
          {timeBoundaryMessage && <Typography variant="copyTwo">{timeBoundaryMessage}</Typography>}
        </TextWrapper>
      </Box>
    </ContentWrapper>
  );
};

export default ItemUnavailableMessage;
